<script lang="ts">
	/**
	 * EssayLayout — standard envelope for notes essays.
	 *
	 * Letterhead → eyebrow → headline → lede → body slot → signature/seal.
	 * Used by every essay under /notes/[slug]/.
	 */

	import type { Snippet } from 'svelte';
	import Letterhead from './Letterhead.svelte';
	import Signature from './Signature.svelte';
	import Seal from './Seal.svelte';

	interface Props {
		title: string;
		lede?: string;
		publishedAt?: string;
		readingTime?: number;
		status?: 'published' | 'scheduled';
		children: Snippet;
	}

	let { title, lede, publishedAt, readingTime, status = 'published', children }: Props = $props();

	const publishedLabel = $derived(
		publishedAt
			? new Date(publishedAt).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric'
				})
			: undefined
	);
</script>

<article class="essay">
	<Letterhead
		eyebrow="ekavi.care · for the practice manager"
		date={readingTime ? `${readingTime} minute read` : undefined}
	>
		<header class="essay-head">
			{#if status === 'scheduled'}
				<p class="status">Scheduled — being written.</p>
			{:else if publishedLabel}
				<p class="status">{publishedLabel}</p>
			{/if}
			<h1 class="essay-title">{title}</h1>
			{#if lede}
				<p class="essay-lede">{lede}</p>
			{/if}
		</header>

		<div class="essay-body">
			{@render children()}
		</div>

		{#if status === 'published'}
			<footer class="essay-foot">
				<hr class="essay-rule" />
				<p class="essay-sign">
					<Signature size="md" />
				</p>
				<p class="essay-seal">
					<Seal />
				</p>
			</footer>
		{/if}
	</Letterhead>
</article>

<style>
	.essay {
		display: block;
	}
	.essay-head {
		margin-bottom: var(--ekavi-space-9);
	}
	.status {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-4);
	}
	.essay-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-40);
		letter-spacing: var(--ekavi-track-headers);
		line-height: var(--ekavi-leading-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		max-width: 24ch;
	}
	@media (max-width: 640px) {
		.essay-title {
			font-size: var(--ekavi-text-28);
		}
	}
	.essay-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-2);
		line-height: var(--ekavi-leading-body);
		margin: 0;
		max-width: 38ch;
	}

	.essay-body :global(p) {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: 1.65;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		max-width: 64ch;
	}
	.essay-body :global(h2) {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-28);
		color: var(--ekavi-ink-1);
		margin: var(--ekavi-space-12) 0 var(--ekavi-space-4);
		max-width: 30ch;
	}
	.essay-body :global(h3) {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-1);
		margin: var(--ekavi-space-9) 0 var(--ekavi-space-3);
	}
	.essay-body :global(em) {
		font-style: italic;
		color: var(--ekavi-ink-1);
	}
	.essay-body :global(strong) {
		font-weight: var(--ekavi-weight-halbfett);
	}
	.essay-body :global(blockquote) {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-2);
		border-left: 2px solid var(--ekavi-charcoal);
		padding: 0 0 0 var(--ekavi-space-4);
		margin: var(--ekavi-space-9) 0;
		max-width: 56ch;
	}
	.essay-body :global(ul),
	.essay-body :global(ol) {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: 1.65;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		padding-left: var(--ekavi-space-6);
		max-width: 60ch;
	}
	.essay-body :global(li) {
		margin-bottom: var(--ekavi-space-2);
	}
	.essay-body :global(hr) {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: var(--ekavi-space-12) 0;
	}
	.essay-body :global(code) {
		font-family: var(--ekavi-font-mono);
		font-size: 0.92em;
		background: var(--ekavi-paper);
		padding: 1px 4px;
	}

	.essay-foot {
		margin-top: var(--ekavi-space-12);
	}
	.essay-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: 0 0 var(--ekavi-space-6);
	}
	.essay-sign {
		margin: 0 0 var(--ekavi-space-3);
	}
	.essay-seal {
		margin: 0;
	}
</style>
